import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Save, Plus, Trash2, FileText } from "lucide-react";

const MAIL = "Mailing Address (if different from above)";

export const FATCA_FIELDS = [
  ["form_type", "Form Type"], ["beneficial_owner_name", "Beneficial Owner Name"], ["country_of_citizenship", "Country of Citizenship"],
  ["permanent_address", "Permanent Residence Address"], ["permanent_city", "City / State / Postal Code"], ["permanent_country", "Country"],
  ["mailing_address", MAIL], ["mailing_city", "Mailing City / State / Postal Code"], ["mailing_country", "Mailing Country"],
  ["us_tin", "U.S. TIN / Tax Reference Number"], ["foreign_tin", "Foreign Tax Identifying Number"], ["ftin_not_legally_required", "FTIN Not Legally Required"],
  ["reference_numbers", "Reference Number(s)"], ["date_of_birth", "Date of Birth"], ["treaty_country", "Treaty Country of Residence"],
  ["treaty_article", "Treaty Article & Paragraph"], ["treaty_withholding_rate", "Treaty Withholding Rate"], ["treaty_income_type", "Treaty Income Type"],
  ["treaty_additional_conditions", "Additional Conditions"], ["signer_name", "Signer Name"],
  ["capacity_certified", "Signer Capacity — I certify that I have the capacity to sign"], ["signature_date", "Signature Date"],
];

export const CRS_FIELDS = [
  ["form_type", "Self-Certification Type"], ["account_holder_name", "Account Holder Name"], ["date_of_birth", "Date of Birth"], ["place_of_birth", "Place / Country of Birth"],
  ["residence_street", "Residence — Number & Street"], ["residence_city", "Residence — City / Town"], ["residence_state", "Residence — State / Province"],
  ["residence_postcode", "Residence — Post Code"], ["residence_country", "Residence — Country"], ["mailing_street", `${MAIL} — Number & Street`],
  ["mailing_city", "Mailing — City / Town"], ["mailing_state", "Mailing — State / Province"], ["mailing_postcode", "Mailing — Post Code"],
  ["mailing_country", "Mailing — Country"], ["us_tax_status", "U.S. Citizenship / Residence Declaration"], ["us_tin", "U.S. TIN / Tax Reference Number"],
  ["tin_unavailable_reason", "Reason TIN / Tax Ref Number Unavailable"],
  ["signer_name", "Signer Name"], ["declaration_date", "Declaration Date"],
];

export const FATCA_ENTITY_FIELDS = [
  ["form_type", "Form Type"], ["entity_name", "Entity Legal Name"], ["country_of_organization", "Country of Incorporation / Organization"],
  ["disregarded_entity_name", "Disregarded Entity Name"], ["chapter3_status", "Entity Type (Chapter 3 Status)"],
  ["hybrid_treaty_claim", "Hybrid Making a Treaty Claim?"],
  ["chapter4_fatca_status", "FATCA Status (Chapter 4)"], ["permanent_address", "Registered Address"], ["permanent_city", "City / State / Postal Code"],
  ["permanent_country", "Country"], ["mailing_address", MAIL], ["mailing_city", "Mailing City / State / Postal Code"],
  ["mailing_country", "Mailing Country"], ["us_tin", "U.S. TIN / Tax Reference Number"], ["giin", "GIIN"], ["foreign_tin", "Foreign TIN"],
  ["ftin_not_legally_required", "FTIN Not Legally Required"], ["reference_numbers", "Reference Number(s)"],
  ["status_certifications", "Status Certifications"],
  ["signer_name", "Signer Name"],
  ["capacity_certified", "Signer Capacity — I certify that I have the capacity to sign"], ["signature_date", "Signature Date"],
];

const PART_III_FIELDS = [
  ["treaty_country", "14a. The beneficial owner is a resident of (treaty country)"],
  ["treaty_limitation_on_benefits", "14b. Limitation on benefits provision"],
  ["treaty_article", "15. Special rates and conditions — Article & paragraph"],
  ["treaty_withholding_rate", "15. Rate of withholding"], ["treaty_income_type", "15. Type of income"],
];

export const CRS_ENTITY_FIELDS = [
  ["form_type", "Self-Certification Type"], ["entity_name", "Entity Legal Name"],
  ["country_of_incorporation", "Country of Incorporation"], ["registered_street", "Registered — Number & Street"],
  ["registered_city", "Registered — City / Town"], ["registered_state", "Registered — State / Province"],
  ["registered_postcode", "Registered — Post Code"], ["registered_country", "Registered — Country"],
  ["mailing_street", `${MAIL} — Number & Street`], ["mailing_city", "Mailing — City / Town"], ["mailing_state", "Mailing — State / Province"],
  ["mailing_postcode", "Mailing — Post Code"], ["mailing_country", "Mailing — Country"], ["us_person_status", "U.S. Person Status"],
  ["us_tin", "U.S. TIN / Tax Reference Number"], ["us_exemption", "U.S. Exemption"], ["fatca_classification", "US FATCA Classification"], ["giin", "GIIN"],
  ["sponsoring_entity_name", "Sponsoring Entity Name"], ["sponsoring_entity_giin", "Sponsoring Entity GIIN"],
  ["tin_unavailable_reason", "Reason TIN / Tax Ref Number Unavailable"], ["crs_classification", "CRS Entity Classification"],
  ["signer_capacity", "Signer Name"], ["declaration_date", "Declaration Date"],
];

const DECLARATION_KEYS = ["signer_name", "signer_capacity", "declaration_date"];

const LIST_FIELDS = {
  "fatca-entity": [["substantial_us_owners", "Substantial U.S. Owners", [["name", "Name"], ["address", "Address"], ["tin", "Tax Reference Number"]]]],
  "crs-entity": [["controlling_persons", "Controlling Persons", [
    ["name", "Full Name"], ["residence_address", "Residence Address"], ["residence_country", "Residence Country"],
    ["postal_code", "Postal Code"], ["mailing_address", MAIL], ["date_of_birth", "Date of Birth"],
    ["place_of_birth", "Place of Birth"], ["entity_names", "Entity Account Holder(s)"], ["controlling_person_type", "Type of Controlling Person"],
    ["tax_residencies", "Tax Residencies (country / type / Tax Reference Number)"],
    ["tin_unavailable_reason", "Reason TIN / Tax Ref Number Unavailable"],
  ]]],
};

export const taxVariant = (data, investorType) =>
  data?.form_variant || (investorType && investorType !== "Individual" ? "entity" : "individual");

const FIELD_SETS = {
  "fatca-individual": FATCA_FIELDS, "crs-individual": CRS_FIELDS,
  "fatca-entity": FATCA_ENTITY_FIELDS, "crs-entity": CRS_ENTITY_FIELDS,
};

const slug = (s) => s.replace(/_/g, "-");

const WIDE_COLS = ["tax_residencies", "tin", "residence_address", "mailing_address", "entity_names", "address", "name", "place_of_birth"];

const ListRows = ({ section, listKey, label, cols, rows, canEdit, onChange }) => (
  <div className="space-y-3 md:col-span-2" data-testid={`${section}-${slug(listKey)}`}>
    <div className="flex items-center justify-between">
      <Label>{label}</Label>
      {canEdit && (
        <Button variant="secondary" size="sm" className="border border-border h-7 text-xs" data-testid={`${section}-add-${slug(listKey)}-btn`}
          onClick={() => onChange([...rows, Object.fromEntries(cols.map(([k]) => [k, ""]))])}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      )}
    </div>
    {rows.length === 0 ? <p className="text-xs text-muted-foreground">None recorded</p> : (
      <div className="overflow-x-auto rounded-lg border border-border/50">
        <table className="w-full text-sm" data-testid={`${section}-${slug(listKey)}-table`}>
          <thead className="bg-secondary/40">
            <tr>
              <th className="px-2 py-2 text-left text-xs font-medium text-muted-foreground w-8">#</th>
              {cols.map(([k, colLabel]) => (
                <th key={k} className={`px-2 py-2 text-left text-xs font-medium text-muted-foreground ${WIDE_COLS.includes(k) ? "min-w-[300px]" : "min-w-[170px]"}`} data-testid={`${section}-${slug(listKey)}-header-${slug(k)}`}>{colLabel}</th>
              ))}
              {canEdit && <th className="w-10" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border/50" data-testid={`${section}-${slug(listKey)}-row-${i}`}>
                <td className="px-2 py-2 text-xs text-muted-foreground">{i + 1}</td>
                {cols.map(([k]) => (
                  <td key={k} className="px-2 py-2">
                    <Input value={r[k] || ""} disabled={!canEdit} className="bg-secondary/50 text-sm" data-testid={`${section}-${slug(listKey)}-${i}-${slug(k)}`}
                      onChange={(e) => onChange(rows.map((x, idx) => (idx === i ? { ...x, [k]: e.target.value } : x)))} />
                  </td>
                ))}
                {canEdit && (
                  <td className="px-1 py-2">
                    <Button variant="ghost" size="icon" className="w-7 h-7 text-muted-foreground hover:text-red-400" data-testid={`${section}-${slug(listKey)}-${i}-remove`}
                      onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const RES_COLS = [["country", "Country"], ["tin_type", "Tax Reference Number Type"], ["tin", "Tax Reference Number"]];

const ResidencyRows = ({ rows, canEdit, onChange }) => (
  <div className="space-y-2 md:col-span-2" data-testid="crs-tax-residencies">
    <div className="flex items-center justify-between">
      <Label className="font-heading text-sm">Declaration of Tax Residency (other than U.S.)</Label>
      {canEdit && (
        <Button variant="secondary" size="sm" className="border border-border h-7 text-xs" data-testid="crs-add-residency-btn"
          onClick={() => onChange([...rows, { country: "", tin_type: "", tin: "" }])}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      )}
    </div>
    {rows.length === 0 && <p className="text-xs text-muted-foreground">No tax residencies recorded</p>}
    {rows.length > 0 && (
      <div className="flex items-center gap-2" data-testid="crs-residency-header">
        {RES_COLS.map(([k, l]) => <span key={k} className={`${k === "tin" ? "flex-[2]" : "flex-1"} text-xs font-medium text-muted-foreground`}>{l}</span>)}
        {canEdit && <span className="w-8 shrink-0" />}
      </div>
    )}
    {rows.map((r, i) => (
      <div key={i} className="flex items-center gap-2" data-testid={`crs-residency-row-${i}`}>
        {RES_COLS.map(([k, l]) => (
          <Input key={k} value={r[k] || ""} disabled={!canEdit} className={`bg-secondary/50 text-sm ${k === "tin" ? "flex-[2]" : "flex-1"}`}
            placeholder={l} title={r[k] || ""}
            data-testid={`crs-residency-${i}-${slug(k)}`}
            onChange={(e) => onChange(rows.map((x, idx) => (idx === i ? { ...x, [k]: e.target.value } : x)))} />
        ))}
        {canEdit && (
          <Button variant="ghost" size="icon" className="w-8 h-8 shrink-0 text-muted-foreground hover:text-red-400" data-testid={`crs-residency-${i}-remove`}
            onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    ))}
  </div>
);

const MULTILINE_KEYS = ["status_certifications"];

export const STATUS_LINES = [
  ["line_40a", "40a — Passive NFFE"],
  ["line_40b", "40b — No substantial U.S. owners (or no controlling U.S. persons)"],
  ["line_40c", "40c — Name, address and TIN of each substantial U.S. owner provided in Part XXIX"],
];

const line40a = (form) => form.line_40a ?? (/\b40a\b|passive\s+nffe/i.test(form.status_certifications || "") ? "Yes" : "");

const StatusCertBox = ({ section, form, setForm, canEdit }) => (
  <div className="md:col-span-2 space-y-4 rounded-lg border border-border/50 p-4" data-testid={`${section}-status-certifications-box`}>
    <Label>Status Certifications</Label>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {STATUS_LINES.map(([key, label]) => (
        <div key={key} className="space-y-2">
          <Label className="text-xs leading-snug">{label}</Label>
          <Input value={key === "line_40a" ? line40a(form) : form[key] || ""} disabled={!canEdit} className="bg-secondary/50" data-testid={`${section}-field-${slug(key)}`}
            onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))} />
        </div>
      ))}
    </div>
  </div>
);

const FieldGrid = ({ section, fields, form, setForm, canEdit }) => fields.map(([key, label]) => key === "status_certifications" ? (
  <StatusCertBox key={key} section={section} form={form} setForm={setForm} canEdit={canEdit} />
) : (
  <div key={key} className={`space-y-2 ${MULTILINE_KEYS.includes(key) ? "md:col-span-2" : ""}`}>
    <Label>{label}</Label>
    {MULTILINE_KEYS.includes(key) ? (
      <Textarea value={String(form[key] || "").split(/;\s*/).join("\n")} disabled={!canEdit} rows={3} className="bg-secondary/50" data-testid={`${section}-field-${slug(key)}`}
        onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value.split("\n").join("; ") }))} />
    ) : (
      <Input value={form[key] || ""} disabled={!canEdit} className="bg-secondary/50" data-testid={`${section}-field-${slug(key)}`}
        onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))} />
    )}
  </div>
));

const SubHeading = ({ children, testId }) => (
  <p className="md:col-span-2 font-heading text-sm font-semibold pt-2 border-t border-border/50" data-testid={testId}>{children}</p>
);

const TITLES = {
  "fatca-individual": "FATCA — Form W-8BEN / W-9", "crs-individual": "CRS — Self-Certification",
  "fatca-entity": "FATCA — Form W-8BEN-E", "crs-entity": "CRS — Entity Self-Certification",
};

export const TaxFormTab = ({ section, investorType, data, canEdit, onSave, headerAction }) => {
  const variant = taxVariant(data, investorType);
  const title = TITLES[`${section}-${variant}`];
  const fields = FIELD_SETS[`${section}-${variant}`];
  const lists = LIST_FIELDS[`${section}-${variant}`] || [];
  const [form, setForm] = useState(data || {});
  const [saving, setSaving] = useState(false);
  const dataKey = JSON.stringify(data || {});
  useEffect(() => { setForm(JSON.parse(dataKey)); }, [dataKey]);
  const hasData = Object.entries(data || {}).filter(([k]) => k !== "form_variant").map(([, v]) => v).some((v) => (Array.isArray(v) ? v.length : String(v || "").trim()));

  const save = async () => {
    setSaving(true);
    const newName = variant === "entity" && (form.entity_name || "").trim() && form.entity_name !== (data || {}).entity_name
      ? form.entity_name.trim() : null;
    try { await onSave(section, { ...form, form_variant: variant }, newName); } finally { setSaving(false); }
  };

  const isCrs = section === "crs";
  const mainFields = isCrs ? fields.filter(([k]) => !DECLARATION_KEYS.includes(k)) : fields;
  const declFields = isCrs ? fields.filter(([k]) => DECLARATION_KEYS.includes(k)) : [];
  const splitAt = isCrs && variant === "entity" ? mainFields.findIndex(([k]) => k === "tin_unavailable_reason") : -1;
  const topFields = splitAt >= 0 ? mainFields.slice(0, splitAt) : mainFields;
  const afterResFields = splitAt >= 0 ? mainFields.slice(splitAt) : [];
  const showPartIII = section === "fatca" && variant === "entity" &&
    (form.hybrid_treaty_claim === "Yes" || PART_III_FIELDS.some(([k]) => String(form[k] || "").trim()));
  const gridProps = { section, form, setForm, canEdit };

  return (
    <Card data-testid={`${section}-tab-card`}>
      <CardHeader className="p-6 pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" /> {title}
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wider" data-testid={`${section}-variant-badge`}>{variant === "entity" ? "Entity" : "Individual"}</Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            {!hasData && <Badge variant="outline" className="text-[10px] uppercase tracking-wider text-amber-400 border-amber-400/30" data-testid={`${section}-empty-badge`}>Not provided</Badge>}
            {headerAction}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Extracted from the uploaded FATCA / CRS file. {canEdit ? "You can correct any value below." : ""}</p>
      </CardHeader>
      <CardContent className="p-6 pt-0 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldGrid fields={topFields} {...gridProps} />
          {showPartIII && (
            <>
              <SubHeading testId="fatca-part-iii-heading">Part III — Claim of Tax Treaty Benefits</SubHeading>
              <FieldGrid fields={PART_III_FIELDS} {...gridProps} />
            </>
          )}
          {isCrs && (
            <ResidencyRows rows={form.tax_residencies || []} canEdit={canEdit}
              onChange={(rows) => setForm((prev) => ({ ...prev, tax_residencies: rows }))} />
          )}
          {afterResFields.length > 0 && <FieldGrid fields={afterResFields} {...gridProps} />}
          {isCrs && (
            <>
              <SubHeading testId="crs-declaration-heading">Declaration and Undertakings</SubHeading>
              <FieldGrid fields={declFields} {...gridProps} />
            </>
          )}
          {lists.map(([key, label, cols]) => (
            <ListRows key={key} section={section} listKey={key} label={label} cols={cols} rows={form[key] || []} canEdit={canEdit}
              onChange={(rows) => setForm((prev) => ({ ...prev, [key]: rows }))} />
          ))}
        </div>
        {canEdit && (
          <div className="flex justify-end pt-2">
            <Button onClick={save} disabled={saving} data-testid={`save-${section}-button`}>
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
